import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  DeleteButton,
  TextInput,
  NumberField,
  NumberInput,
  BooleanField,
  BooleanInput,
  required,
  regex,
  useTranslate,
  useRecordContext,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import EmptyList from '../components/misc/EmptyList';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';
import { EditableImage } from '../components/form/EditableImageField';
import { resolveMediaUrl } from '../utils/mediaUrl';

const entity = 'utilityBarItems';

const ICON_SIZE = 128;
const ICON_ASPECT = 1;

/** react-admin'de `url` validator yok — regex ile */
const urlValidate = regex(/^(https?:\/\/)[^\s]+$/i, 'Geçerli bir URL girin (https://...)');

const ColorFieldInput = ({ source, label, defaultValue = '#2E9B4B' }) => {
  const { setValue } = useFormContext();
  const value = useWatch({ name: source }) || defaultValue;
  const swatch = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : defaultValue;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 1, width: '100%' }}>
      <TextInput
        source={source}
        label={label}
        fullWidth
        defaultValue={defaultValue}
        helperText="Örn. #2E9B4B"
      />
      <Box
        component="input"
        type="color"
        value={swatch}
        onChange={(e) => {
          setValue(source, e.target.value, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }}
        sx={{
          width: 48,
          height: 40,
          border: '1px solid #ddd',
          borderRadius: 1,
          cursor: 'pointer',
          p: 0.25,
          mb: 2.5,
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const iconPreviewSrc = (icon) => {
  if (!icon) return null;
  if (typeof icon === 'string') return resolveMediaUrl(icon);
  if (icon?.src) {
    if (typeof icon.src === 'string' && icon.src.startsWith('blob:')) return icon.src;
    return resolveMediaUrl(icon.src);
  }
  if (icon?.rawFile instanceof Blob) {
    try {
      return URL.createObjectURL(icon.rawFile);
    } catch {
      return null;
    }
  }
  return null;
};

const UtilityBarLivePreview = () => {
  const translate = useTranslate();
  const title = useWatch({ name: 'title' });
  const subtitle = useWatch({ name: 'subtitle' });
  const hoverColor = useWatch({ name: 'hoverColor' }) || '#2E9B4B';
  const icon = useWatch({ name: 'icon' });
  const href = useWatch({ name: 'href' });

  const titleText =
    (typeof title === 'object' ? title?.tr || title?.en : title) ||
    translate('utilityBarItems.preview_title', { _: 'Başlık' });
  const subtitleText =
    (typeof subtitle === 'object' ? subtitle?.tr || subtitle?.en : subtitle) ||
    translate('utilityBarItems.preview_subtitle', { _: 'Alt başlık' });
  const imgSrc = iconPreviewSrc(icon);

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: '#E6E6E6',
        border: '1px solid #E8E8E8',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#555' }}>
        {translate('utilityBarItems.preview', { _: 'Önizleme' })}
      </Typography>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          bgcolor: '#fff',
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: hoverColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {imgSrc ? (
            <Box
              component="img"
              src={imgSrc}
              alt=""
              sx={{
                width: 22,
                height: 22,
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
          ) : (
            <Box
              sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1A1A1A', lineHeight: 1.2 }}>
            {titleText}
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#9E9E9E', lineHeight: 1.2 }}>
            {subtitleText}
          </Typography>
          {href ? (
            <Typography sx={{ fontSize: 9, color: '#bbb', mt: 0.25, wordBreak: 'break-all' }}>
              {href}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const FormToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton alwaysEnable />
  </Toolbar>
);

const UtilityBarFormFields = ({ isEdit = false }) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
        {translate('utilityBarItems.form_heading', { _: 'Utility Bar Öğesi' })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {translate('utilityBarItems.form_subheading', {
          _: 'Web üst şeritteki linkler. İkon, yazı ve hover rengini buradan yönetin.',
        })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
          width: '100%',
        }}
      >
        <Box>
          <TextInput
            source="name"
            label={translate('utilityBarItems.name', { _: 'Dahili Ad' })}
            fullWidth
            validate={required()}
            helperText={translate('utilityBarItems.name_helper', {
              _: 'Sadece panel listesinde görünür',
            })}
          />
          <NumberInput
            source="order"
            label={translate('utilityBarItems.order', { _: 'Sıra' })}
            fullWidth
            defaultValue={0}
          />
          <BooleanInput
            source="isActive"
            label={translate('utilityBarItems.is_active', { _: 'Aktif?' })}
            defaultValue={true}
          />

          <EditableImage
            source="icon"
            record={record}
            aspect={ICON_ASPECT}
            width={ICON_SIZE}
            height={ICON_SIZE}
            outputMime="image/png"
            label={translate('utilityBarItems.icon', { _: 'İkon' })}
            hint={translate('utilityBarItems.icon_hint', {
              _: 'Kare (1:1) şeffaf PNG ikon önerilir. Hover’da renkli daire + beyaz silüet olur.',
            })}
            cropTitle={translate('utilityBarItems.icon_crop_title', {
              _: 'İkon Kırp (1:1)',
            })}
            cropDesc={translate('utilityBarItems.icon_crop_desc', {
              _: 'Kırpma alanı kare oranında kilitli. Şeffaflık korunur (PNG).',
            })}
          />

          <TextInput
            source={localizedSource('title')}
            label={translate('utilityBarItems.title_field', { _: 'Başlık' })}
            fullWidth
            validate={required()}
          />
          <TextInput
            source={localizedSource('subtitle')}
            label={translate('utilityBarItems.subtitle', { _: 'Alt Başlık' })}
            fullWidth
          />
          <TextInput
            source="href"
            label={translate('utilityBarItems.href', { _: 'Link (URL)' })}
            fullWidth
            validate={[required(), urlValidate]}
            helperText={translate('utilityBarItems.href_helper', {
              _: 'Örn. https://kuponkibris.com',
            })}
          />
          <ColorFieldInput
            source="hoverColor"
            label={translate('utilityBarItems.hover_color', { _: 'Hover Rengi' })}
            defaultValue="#2E9B4B"
          />
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
          <UtilityBarLivePreview />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const UtilityBarItemList = (props) => {
  const translate = useTranslate();
  return (
    <List
      {...props}
      title={translate('utilityBarItems.title', { _: 'Utility Bar' })}
      empty={<EmptyList entity={entity} />}
      sort={{ field: 'order', order: 'ASC' }}
      exporter={false}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label={translate('utilityBarItems.name', { _: 'Ad' })} />
        <NumberField source="order" label={translate('utilityBarItems.order', { _: 'Sıra' })} />
        <BooleanField
          source="isActive"
          label={translate('utilityBarItems.is_active', { _: 'Aktif?' })}
        />
        <EditButton />
        <DeleteButton basePath={`/${entity}`} />
      </Datagrid>
    </List>
  );
};

export const UtilityBarItemEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('utilityBarItems.edit_title', { _: 'Utility Bar Düzenle' })}
      {...props}
      mutationMode="pessimistic"
    >
      <SimpleForm redirect={`/${entity}`} toolbar={<FormToolbar />}>
        <UtilityBarFormFields isEdit />
      </SimpleForm>
    </Edit>
  );
};

export const UtilityBarItemCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create
      title={translate('utilityBarItems.create_title', { _: 'Yeni Utility Bar Öğesi' })}
      {...props}
    >
      <SimpleForm
        redirect={`/${entity}`}
        toolbar={<FormToolbar />}
        defaultValues={{
          isActive: true,
          order: 0,
          hoverColor: '#2E9B4B',
        }}
      >
        <UtilityBarFormFields />
      </SimpleForm>
    </Create>
  );
};
